import React, { useState, useEffect, useRef } from 'react';

const phases = [
  {
    label: 'Inbound Call',
    icon: 'fa-phone',
    color: 'bg-[#055EFD]',
    messages: [
      {
        type: 'caller',
        text: '"Hi, I was in the boardroom about an hour ago for a meeting and I think I left my wallet there. Has anyone seen it?"',
      },
      {
        type: 'agent',
        text: '"I\'ll check with the team right now. Let me reach out to the staff and see if anyone has found it."',
      },
    ],
  },
  {
    label: 'Simultaneous SMS to Staff',
    icon: 'fa-comments',
    color: 'bg-green-600',
    messages: [
      {
        type: 'sms-out',
        recipients: ['Front Desk', 'Facilities', 'Security'],
        text: 'Hi, a caller is asking about a wallet left in the boardroom about an hour ago. Has anyone seen it?',
      },
    ],
  },
  {
    label: 'Staff Responds During Call',
    icon: 'fa-reply',
    color: 'bg-amber-500',
    messages: [
      {
        type: 'sms-in',
        from: 'Security',
        text: 'Yes! Found a brown leather wallet in the boardroom. I have it at the security desk.',
      },
      {
        type: 'agent',
        text: '"Great news! Security just confirmed they found a brown leather wallet in the boardroom. It\'s at the security desk whenever you\'re ready to pick it up."',
      },
      {
        type: 'caller',
        text: '"Oh perfect, thank you so much!"',
      },
    ],
  },
  {
    label: 'Async Follow-up (if staff responds after call)',
    icon: 'fa-clock-o',
    color: 'bg-purple-600',
    messages: [
      {
        type: 'note',
        text: 'If no staff responds before the call ends, the agent sends an SMS to the caller as soon as a staff member replies:',
      },
      {
        type: 'sms-followup',
        text: 'Update on your wallet: Security found a brown leather wallet in the boardroom. It\'s at the security desk. — Remote Assistant',
      },
    ],
  },
];

const channelLabels = [
  { icon: 'fa-phone', label: 'Voice (PSTN)', color: 'text-[#055EFD]' },
  { icon: 'fa-comment', label: 'SMS', color: 'text-green-400' },
  { icon: 'fa-envelope', label: 'Email', color: 'text-amber-400' },
];

const Showcase = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isAutoPlaying) return;
    intervalRef.current = setInterval(() => {
      setActivePhase((prev) => (prev + 1) % phases.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [isAutoPlaying]);

  const handlePhaseClick = (index) => {
    setActivePhase(index);
    setIsAutoPlaying(false);
    clearInterval(intervalRef.current);
  };

  const current = phases[activePhase];

  return (
    <div className="px-8 py-20 bg-neutral-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#055EFD]/10 border border-[#055EFD]/20 rounded-full mb-4">
            <span className="text-[#0AF1FE] text-xs font-bold font-['Inter'] tracking-wider uppercase">Live Scenario</span>
          </div>
          <h2 className="!text-white text-3xl lg:text-4xl font-bold font-['Montserrat'] mb-4">
            The Lost Wallet Scenario
          </h2>
          <p className="!text-stone-300 text-lg font-medium font-['Inter'] max-w-3xl mx-auto">
            A single interaction that demonstrates simultaneous voice, SMS, and real-time staff coordination — something no other AI platform can do.
          </p>
        </div>

        {/* Active channels indicator */}
        <div className="flex justify-center gap-6 mb-12">
          {channelLabels.map((ch) => (
            <div key={ch.label} className="flex items-center gap-2">
              <i className={`fa ${ch.icon} ${ch.color} text-sm`}></i>
              <span className="text-stone-400 text-xs font-medium font-['Inter']">{ch.label}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Phase selector */}
          <div className="lg:w-1/3 flex flex-col gap-2">
            {phases.map((phase, index) => (
              <button
                key={index}
                onClick={() => handlePhaseClick(index)}
                className={`text-left p-4 rounded-lg transition-all relative ${
                  activePhase === index
                    ? 'bg-neutral-800 border border-neutral-600'
                    : 'bg-transparent hover:bg-neutral-800/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 ${phase.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <i className={`fa ${phase.icon} text-white text-xs`}></i>
                  </div>
                  <div>
                    <span className={`font-bold font-['Montserrat'] text-sm ${
                      activePhase === index ? 'text-white' : 'text-stone-400'
                    }`}>
                      {phase.label}
                    </span>
                  </div>
                </div>
                {activePhase === index && isAutoPlaying && (
                  <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-neutral-700 rounded overflow-hidden">
                    <div className="h-full bg-[#055EFD] rounded animate-progress"></div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Conversation view */}
          <div className="lg:w-2/3">
            <div className="bg-neutral-800 rounded-lg border border-neutral-700 p-6 min-h-[360px]">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-neutral-700">
                <div className={`w-6 h-6 ${current.color} rounded-full flex items-center justify-center`}>
                  <i className={`fa ${current.icon} text-white text-[10px]`}></i>
                </div>
                <span className="text-white text-sm font-bold font-['Montserrat']">{current.label}</span>
              </div>

              <div className="flex flex-col gap-4">
                {current.messages.map((msg, i) => {
                  if (msg.type === 'caller') {
                    return (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-8 h-8 bg-[#055EFD] rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fa fa-user text-white text-xs"></i>
                        </div>
                        <div>
                          <div className="text-stone-500 text-[10px] font-['Inter'] mb-1 flex items-center gap-1">
                            <i className="fa fa-phone text-[8px]"></i> Caller (Voice)
                          </div>
                          <div className="bg-[#055EFD] text-white rounded-lg rounded-tl-none p-3 inline-block">
                            <p className="text-sm font-medium font-['Inter']">{msg.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (msg.type === 'agent') {
                    return (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fa fa-robot text-white text-xs"></i>
                        </div>
                        <div>
                          <div className="text-stone-500 text-[10px] font-['Inter'] mb-1 flex items-center gap-1">
                            <i className="fa fa-phone text-[8px]"></i> AI Agent (Voice)
                          </div>
                          <div className="bg-neutral-700 text-stone-200 rounded-lg rounded-tl-none p-3 inline-block">
                            <p className="text-sm font-medium font-['Inter']">{msg.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (msg.type === 'sms-out') {
                    return (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fa fa-comment text-white text-xs"></i>
                        </div>
                        <div className="flex-1">
                          <div className="text-stone-500 text-[10px] font-['Inter'] mb-1 flex items-center gap-1">
                            <i className="fa fa-comment text-[8px]"></i> SMS Broadcast (simultaneous)
                          </div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {msg.recipients.map((r) => (
                              <span key={r} className="px-2 py-0.5 bg-green-600/20 text-green-400 text-[10px] font-bold font-['Inter'] rounded-full border border-green-600/30">
                                {r}
                              </span>
                            ))}
                          </div>
                          <div className="bg-green-900/30 border border-green-700/30 text-green-200 rounded-lg p-3 inline-block">
                            <p className="text-sm font-medium font-['Inter']">{msg.text}</p>
                          </div>
                          <div className="mt-2 flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-[#055EFD] rounded-full animate-pulse"></div>
                            <span className="text-stone-500 text-[10px] font-['Inter']">Voice call still active with caller</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (msg.type === 'sms-in') {
                    return (
                      <div key={i} className="flex gap-3 items-start justify-end">
                        <div className="text-right">
                          <div className="text-stone-500 text-[10px] font-['Inter'] mb-1 flex items-center gap-1 justify-end">
                            <i className="fa fa-comment text-[8px]"></i> {msg.from} (SMS Reply)
                          </div>
                          <div className="bg-amber-900/30 border border-amber-700/30 text-amber-200 rounded-lg rounded-tr-none p-3 inline-block">
                            <p className="text-sm font-medium font-['Inter']">{msg.text}</p>
                          </div>
                        </div>
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fa fa-shield text-white text-xs"></i>
                        </div>
                      </div>
                    );
                  }

                  if (msg.type === 'note') {
                    return (
                      <div key={i} className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3">
                        <p className="text-purple-300 text-xs font-medium font-['Inter'] flex items-center gap-2">
                          <i className="fa fa-info-circle"></i>
                          {msg.text}
                        </p>
                      </div>
                    );
                  }

                  if (msg.type === 'sms-followup') {
                    return (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="fa fa-comment text-white text-xs"></i>
                        </div>
                        <div>
                          <div className="text-stone-500 text-[10px] font-['Inter'] mb-1 flex items-center gap-1">
                            <i className="fa fa-comment text-[8px]"></i> SMS to Caller (post-call)
                          </div>
                          <div className="bg-purple-900/30 border border-purple-700/30 text-purple-200 rounded-lg rounded-tl-none p-3 inline-block">
                            <p className="text-sm font-medium font-['Inter']">{msg.text}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom callout */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-neutral-800 border border-neutral-700 rounded-lg px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-1">
                <div className="w-5 h-5 bg-[#055EFD] rounded-full border-2 border-neutral-800 flex items-center justify-center">
                  <i className="fa fa-phone text-white text-[8px]"></i>
                </div>
                <div className="w-5 h-5 bg-green-600 rounded-full border-2 border-neutral-800 flex items-center justify-center">
                  <i className="fa fa-comment text-white text-[8px]"></i>
                </div>
                <div className="w-5 h-5 bg-amber-500 rounded-full border-2 border-neutral-800 flex items-center justify-center">
                  <i className="fa fa-envelope text-white text-[8px]"></i>
                </div>
              </div>
              <p className="!text-stone-300 text-sm font-medium font-['Inter']">
                Voice, SMS, and email operating concurrently — not handed off between systems.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Showcase;
